import holdcoLogoUrl from "@hyperoll/design/assets/logos/Holdco-Black-Logo.png";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@hyperoll/ui";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import {
  ArrowDownRight,
  ArrowUpRight,
  Check,
  CircleDollarSign,
  Clock3,
  Gauge,
  ListTodo,
  LogOut,
  Medal,
  Plus,
  RefreshCw,
  Settings,
  Trophy,
  UserPlus,
  Users,
} from "lucide-react";
import {
  createContext,
  type FormEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, ApiError } from "./api";
import type { DashboardData, OpenItem, SessionUser } from "./types";

type AuthContextValue = {
  user: SessionUser | null;
  setUser: (user: SessionUser | null) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("Auth context is unavailable");
  return value;
}

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const compactCurrency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

const formatTime = (date: string) =>
  new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(new Date(date));

function elapsedHours(date: string) {
  return Math.max(0, (Date.now() - Date.parse(date)) / 3_600_000);
}

function ageLabel(date: string) {
  const hours = elapsedHours(date);
  if (hours < 1) return `${Math.max(1, Math.floor(hours * 60))}m open`;
  if (hours < 24) return `${Math.floor(hours)}h open`;
  const days = Math.floor(hours / 24);
  const remainder = Math.floor(hours % 24);
  return `${days}d ${remainder}h open`;
}

function Brand() {
  return (
    <div className="brand-lockup">
      <img src={holdcoLogoUrl} alt="Hype Pharm" />
      <span aria-hidden="true" />
      <div>
        <strong>REAL ROOFING</strong>
        <small>COMPANY DASHBOARD</small>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <main className="loading-screen">
      <Brand />
      <div className="loading-mark"><RefreshCw aria-hidden="true" /></div>
      <p>Connecting to the company pulse…</p>
    </main>
  );
}

function AuthenticationScreen({ onAuthenticated }: { onAuthenticated: (user: SessionUser) => void }) {
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.bootstrapStatus()
      .then((status) => setNeedsBootstrap(status.needsBootstrap))
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : "Unable to connect"))
      .finally(() => setReady(true));
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const result = needsBootstrap
        ? await api.bootstrap(email, password)
        : await api.login(email, password);
      onAuthenticated(result.user);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to sign in");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-screen">
      <section className="auth-story">
        <Brand />
        <div className="auth-copy">
          <Badge className="live-badge">LIVE COMPANY PULSE</Badge>
          <h1>Keep the number<br />in the room.</h1>
          <p>Month-to-date sales performance, the revenue goal, and every open commitment—always visible.</p>
        </div>
        <div className="auth-grid" aria-hidden="true" />
      </section>
      <section className="auth-form-wrap">
        <form className="auth-form" onSubmit={submit}>
          <div className="auth-form-heading">
            <small>{needsBootstrap ? "FIRST-TIME SETUP" : "SECURE ACCESS"}</small>
            <h2>{needsBootstrap ? "Create the first login" : "Welcome back"}</h2>
            <p>{needsBootstrap ? "This account can add the rest of the team." : "Sign in to open the dashboard."}</p>
          </div>
          <div className="field-stack">
            <Label htmlFor="email">Email address</Label>
            <Input id="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required />
          </div>
          <div className="field-stack">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={needsBootstrap ? "new-password" : "current-password"} minLength={8} required />
          </div>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <Button type="submit" size="lg" disabled={!ready || submitting}>
            {submitting ? <RefreshCw className="spin" /> : null}
            {needsBootstrap ? "Create login" : "Open dashboard"}
          </Button>
        </form>
      </section>
    </main>
  );
}

function AppFrame({ children }: { children: ReactNode }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  async function logout() {
    await api.logout().catch(() => undefined);
    setUser(null);
    void navigate({ to: "/" });
  }

  return (
    <div className="app-frame">
      <header className="topbar">
        <Brand />
        <nav aria-label="Dashboard navigation">
          <Link to="/" activeOptions={{ exact: true }}><Gauge />Dashboard</Link>
          <Link to="/items"><ListTodo />Items</Link>
          <Link to="/settings"><Settings />Manage</Link>
        </nav>
        <div className="account-menu">
          <span>{user?.email}</span>
          <Button variant="ghost" size="icon" aria-label="Sign out" onClick={logout}><LogOut /></Button>
        </div>
      </header>
      {children}
    </div>
  );
}

function RootComponent() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    api.session()
      .then((result) => setUser(result.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingScreen />;
  if (!user) return <AuthenticationScreen onAuthenticated={setUser} />;

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <AppFrame key={location.pathname}><Outlet /></AppFrame>
    </AuthContext.Provider>
  );
}

function MetricError({ message, retry }: { message: string; retry: () => void }) {
  return (
    <main className="page-state">
      <CircleDollarSign />
      <h1>We lost the live feed.</h1>
      <p>{message}</p>
      <Button onClick={retry}><RefreshCw />Try again</Button>
    </main>
  );
}

function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [, setClock] = useState(0);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setRefreshing(true);
    try {
      setData(await api.dashboard());
      setError("");
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) window.location.reload();
      setError(caught instanceof Error ? caught.message : "Unable to load dashboard");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const refresh = window.setInterval(() => void load(true), 5 * 60 * 1_000);
    const clock = window.setInterval(() => setClock((value) => value + 1), 60 * 1_000);
    return () => {
      window.clearInterval(refresh);
      window.clearInterval(clock);
    };
  }, [load]);

  if (!data && error) return <MetricError message={error} retry={() => void load()} />;
  if (!data) return <LoadingScreen />;

  const cappedProgress = Math.min(100, Math.max(0, data.goalProgress));
  const totalLeads = data.leaderboard.reduce((sum, rep) => sum + rep.leads, 0);
  const totalCloses = data.leaderboard.reduce((sum, rep) => sum + rep.closes, 0);
  const companyCloseRate = totalLeads ? Math.round((totalCloses / totalLeads) * 100) : 0;

  return (
    <main className="dashboard-page">
      <section className="dashboard-heading">
        <div>
          <p className="eyebrow">{data.monthLabel.toUpperCase()} · MONTH TO DATE</p>
          <h1>The company pulse.</h1>
        </div>
        <div className="refresh-status">
          <span><i /> LIVE</span>
          <small>Updated {formatTime(data.refreshedAt)}</small>
          <Button variant="ghost" size="icon" onClick={() => void load()} aria-label="Refresh dashboard" disabled={refreshing}>
            <RefreshCw className={refreshing ? "spin" : ""} />
          </Button>
        </div>
      </section>

      {error ? <div className="inline-warning">Showing the last available snapshot. {error}</div> : null}

      <section className="goal-panel">
        <div className="goal-summary">
          <p>REVENUE TO GOAL</p>
          <strong>{currency.format(data.totalRevenue)}</strong>
          <span>of {currency.format(data.goal)} company goal</span>
        </div>
        <div className="goal-progress-wrap">
          <div className="goal-progress-meta">
            <strong>{data.goalProgress.toFixed(1)}%</strong>
            <span>{data.daysRemaining} {data.daysRemaining === 1 ? "day" : "days"} left</span>
          </div>
          <div className="goal-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={cappedProgress}>
            <div style={{ width: `${cappedProgress}%` }} />
          </div>
          <div className="goal-foot">
            <span>{data.remainingToGoal > 0 ? `${currency.format(data.remainingToGoal)} remaining` : "Goal cleared"}</span>
            <span>{data.goalProgress >= 100 ? <><ArrowUpRight /> Ahead</> : <><ArrowDownRight /> Keep pushing</>}</span>
          </div>
        </div>
        <div className="company-stat">
          <small>COMPANY CLOSE RATE</small>
          <strong>{companyCloseRate}%</strong>
          <span>{totalCloses} closes / {totalLeads} leads</span>
        </div>
      </section>

      <section className="dashboard-grid">
        <Card className="leaderboard-card">
          <CardHeader className="section-heading">
            <div>
              <CardDescription>SALES PERFORMANCE</CardDescription>
              <CardTitle>Leaderboard</CardTitle>
            </div>
            <Badge variant="secondary"><Trophy /> Ranked by revenue</Badge>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="rank-cell">Rank</TableHead>
                  <TableHead>Sales rep</TableHead>
                  <TableHead className="numeric">Leads</TableHead>
                  <TableHead className="numeric">Closes</TableHead>
                  <TableHead className="numeric">Close rate</TableHead>
                  <TableHead className="numeric">$/Lead</TableHead>
                  <TableHead className="numeric">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.leaderboard.map((rep) => (
                  <TableRow key={rep.salesRep} className={rep.rank <= 3 ? "top-rep" : ""}>
                    <TableCell className="rank-cell">
                      <span className={`rank-badge rank-${rep.rank}`}>
                        {rep.rank <= 3 ? <Medal /> : rep.rank}
                      </span>
                    </TableCell>
                    <TableCell><strong className="rep-name">{rep.salesRep}</strong></TableCell>
                    <TableCell className="numeric">{rep.leads}</TableCell>
                    <TableCell className="numeric">{rep.closes}</TableCell>
                    <TableCell className="numeric"><span className="rate-pill">{rep.closeRate}%</span></TableCell>
                    <TableCell className="numeric">{currency.format(rep.revenuePerLead)}</TableCell>
                    <TableCell className="numeric revenue-cell">{currency.format(rep.revenue)}</TableCell>
                  </TableRow>
                ))}
                {!data.leaderboard.length ? (
                  <TableRow><TableCell colSpan={7} className="empty-row">No jobs have been created this month yet.</TableCell></TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="open-items-card">
          <CardHeader className="section-heading">
            <div>
              <CardDescription>ACCOUNTABILITY</CardDescription>
              <CardTitle>Open items</CardTitle>
            </div>
            <Badge variant="secondary">{data.openItems.length} open</Badge>
          </CardHeader>
          <CardContent className="open-items-list">
            {data.openItems.map((item) => <OpenItemRow key={item.id} item={item} />)}
            {!data.openItems.length ? (
              <div className="all-clear"><Check /><strong>All clear</strong><span>No open items right now.</span></div>
            ) : null}
          </CardContent>
        </Card>
      </section>
    </main>
  );
}

function OpenItemRow({ item, onComplete }: { item: OpenItem; onComplete?: (item: OpenItem) => void }) {
  const overdue = !item.completedAt && elapsedHours(item.createdAt) > 48;
  return (
    <article className={`open-item ${overdue ? "overdue" : ""} ${item.completedAt ? "completed" : ""}`}>
      <div className="open-item-topline">
        <span className="project-label">{item.project}</span>
        <span className="age-label"><Clock3 />{item.completedAt ? "Closed" : ageLabel(item.createdAt)}</span>
      </div>
      <h3>{item.title}</h3>
      <div className="open-item-owner">
        <span>{item.assignedUserEmail.slice(0, 1).toUpperCase()}</span>
        <small>{item.assignedUserEmail}</small>
        {onComplete && !item.completedAt ? (
          <Button variant="ghost" size="sm" onClick={() => onComplete(item)}><Check />Close</Button>
        ) : null}
      </div>
    </article>
  );
}

function PageHeading({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return (
    <section className="secondary-heading">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p>{description}</p>
    </section>
  );
}

function ItemsPage() {
  const [items, setItems] = useState<OpenItem[]>([]);
  const [users, setUsers] = useState<SessionUser[]>([]);
  const [title, setTitle] = useState("");
  const [project, setProject] = useState("");
  const [assignedUserId, setAssignedUserId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [itemResult, userResult] = await Promise.all([api.items(), api.users()]);
      setItems(itemResult.items);
      setUsers(userResult.users);
      setAssignedUserId((current) => current || userResult.users[0]?.id || "");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load items");
    }
  }, []);

  useEffect(() => void load(), [load]);

  async function addItem(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { item } = await api.addItem({ title, project, assignedUserId });
      setItems((current) => [...current, item].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)));
      setTitle("");
      setProject("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to add item");
    } finally {
      setSaving(false);
    }
  }

  async function completeItem(item: OpenItem) {
    const result = await api.setItemCompleted(item.id, true);
    setItems((current) => current.map((candidate) => candidate.id === item.id ? result.item : candidate));
  }

  const open = items.filter((item) => !item.completedAt);
  const completed = items.filter((item) => item.completedAt).reverse();

  return (
    <main className="secondary-page">
      <PageHeading eyebrow="ACCOUNTABILITY" title="Open items" description="Small commitments stay visible until somebody closes the loop." />
      <div className="management-grid">
        <Card>
          <CardHeader><CardTitle>Add an item</CardTitle><CardDescription>It appears on the TV dashboard immediately.</CardDescription></CardHeader>
          <CardContent>
            <form className="management-form" onSubmit={addItem}>
              <div className="field-stack"><Label htmlFor="item-title">Title</Label><Input id="item-title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Confirm supplement approval" required /></div>
              <div className="field-stack"><Label htmlFor="item-project">Project</Label><Input id="item-project" value={project} onChange={(event) => setProject(event.target.value)} placeholder="Wilson residence" required /></div>
              <div className="field-stack"><Label htmlFor="item-user">Assigned user</Label><select id="item-user" value={assignedUserId} onChange={(event) => setAssignedUserId(event.target.value)} required>{users.map((user) => <option key={user.id} value={user.id}>{user.email}</option>)}</select></div>
              {error ? <p className="form-error" role="alert">{error}</p> : null}
              <Button type="submit" disabled={saving || !users.length}><Plus />Add item</Button>
            </form>
          </CardContent>
        </Card>
        <div className="item-management-list">
          <div className="list-heading"><h2>Open</h2><Badge variant="secondary">{open.length}</Badge></div>
          {open.map((item) => <OpenItemRow key={item.id} item={item} onComplete={(target) => void completeItem(target)} />)}
          {!open.length ? <div className="empty-panel">No open items.</div> : null}
          {completed.length ? <div className="list-heading completed-heading"><h2>Recently closed</h2><Badge variant="secondary">{completed.length}</Badge></div> : null}
          {completed.slice(0, 10).map((item) => <OpenItemRow key={item.id} item={item} />)}
        </div>
      </div>
    </main>
  );
}

function SettingsPage() {
  const [users, setUsers] = useState<SessionUser[]>([]);
  const [goal, setGoal] = useState(0);
  const [goalInput, setGoalInput] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.users(), api.dashboard()])
      .then(([userResult, dashboard]) => {
        setUsers(userResult.users);
        setGoal(dashboard.goal);
        setGoalInput(String(dashboard.goal));
      })
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : "Unable to load settings"));
  }, []);

  async function saveGoal(event: FormEvent) {
    event.preventDefault();
    setError("");
    const result = await api.setGoal(Number(goalInput.replace(/[^0-9.]/g, "")));
    setGoal(result.goal);
    setGoalInput(String(result.goal));
    setMessage("Company goal updated.");
  }

  async function addUser(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      const result = await api.addUser(email, password);
      setUsers((current) => [...current, result.user].sort((a, b) => a.email.localeCompare(b.email)));
      setEmail("");
      setPassword("");
      setMessage("New login created.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to add user");
    }
  }

  return (
    <main className="secondary-page">
      <PageHeading eyebrow="MANAGE" title="Dashboard controls" description="Keep the company target and access list current." />
      {message ? <div className="success-message"><Check />{message}</div> : null}
      {error ? <div className="inline-warning">{error}</div> : null}
      <div className="settings-grid">
        <Card>
          <CardHeader><div className="settings-icon"><Trophy /></div><CardTitle>Company revenue goal</CardTitle><CardDescription>One live target for the current dashboard.</CardDescription></CardHeader>
          <CardContent>
            <form className="management-form" onSubmit={saveGoal}>
              <div className="current-setting"><small>CURRENT GOAL</small><strong>{compactCurrency.format(goal)}</strong></div>
              <div className="field-stack"><Label htmlFor="goal">New goal</Label><div className="currency-input"><span>$</span><Input id="goal" inputMode="decimal" value={goalInput} onChange={(event) => setGoalInput(event.target.value)} required /></div></div>
              <Button type="submit">Save goal</Button>
            </form>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><div className="settings-icon"><UserPlus /></div><CardTitle>Add a login</CardTitle><CardDescription>Every login can manage users, items, and the goal.</CardDescription></CardHeader>
          <CardContent>
            <form className="management-form" onSubmit={addUser}>
              <div className="field-stack"><Label htmlFor="new-email">Email</Label><Input id="new-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></div>
              <div className="field-stack"><Label htmlFor="new-password">Temporary password</Label><Input id="new-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required /></div>
              <Button type="submit"><UserPlus />Create login</Button>
            </form>
          </CardContent>
        </Card>
        <Card className="users-card">
          <CardHeader><div className="settings-icon"><Users /></div><CardTitle>Active logins</CardTitle><CardDescription>{users.length} {users.length === 1 ? "person has" : "people have"} dashboard access.</CardDescription></CardHeader>
          <CardContent className="user-list">
            {users.map((user) => <div key={user.id}><span>{user.email.slice(0, 1).toUpperCase()}</span><strong>{user.email}</strong><small>Added {new Date(user.createdAt).toLocaleDateString()}</small></div>)}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

const rootRoute = createRootRoute({ component: RootComponent });
const dashboardRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: DashboardPage });
const itemsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/items", component: ItemsPage });
const settingsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/settings", component: SettingsPage });

const routeTree = rootRoute.addChildren([dashboardRoute, itemsRoute, settingsRoute]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
